/**
 * Work Item Types
 * Types for work items, changes, and completion criteria (used by agent/planning).
 */

import { z } from 'zod';

export const WorkItemStatusSchema = z.enum(['backlog', 'in-progress', 'completed']);
export type WorkItemStatus = z.infer<typeof WorkItemStatusSchema>;

export const WorkItemProgressSchema = z.object({
  remainingCriteria: z.array(z.string()).default([]),
  openQuestions: z.array(z.string()).default([]),
  assignedTo: z.enum(['agent', 'human-product', 'human-expert']).optional(),
});
export type WorkItemProgress = z.infer<typeof WorkItemProgressSchema>;

export const ChangeSchema = z.object({
  id: z.string(),
  description: z.string(),
  buildingBlockId: z.string(),
  buildingBlockType: z.enum(['function', 'workflow', 'connector', 'ui-component', 'app-fragment', 'application', 'project']),
  action: z.enum(['create', 'update', 'split', 'combine']),
});
export type Change = z.infer<typeof ChangeSchema>;

export const CompletionCriterionSchema = z.object({
  id: z.string(),
  description: z.string(),
  type: z.enum(['write-code', 'write-tests', 'verify-tests', 'other']),
  verified: z.boolean().default(false),
});
export type CompletionCriterion = z.infer<typeof CompletionCriterionSchema>;

export const WorkItemPrioritySchema = z.enum(['low', 'medium', 'high', 'critical']);
export type WorkItemPriority = z.infer<typeof WorkItemPrioritySchema>;

export const IterationPhaseSchema = z.enum(['analysis', 'implementation', 'verify', 'reprioritize', 'completed']);
export type IterationPhase = z.infer<typeof IterationPhaseSchema>;

export const WorkItemDependencySchema = z.object({
  type: z.enum(['work-item']),
  id: z.string(),
});
export type WorkItemDependency = z.infer<typeof WorkItemDependencySchema>;

export const WorkItemSchema = z.object({
  id: z.string(),
  sessionId: z.string().optional(),
  summary: z.string().optional(),
  description: z.string(),
  status: WorkItemStatusSchema,
  buildingBlockId: z.string().optional(),
  buildingBlockType: z.enum(['function', 'workflow', 'connector', 'ui-component', 'app-fragment', 'application', 'project']),
  changes: z.array(ChangeSchema).default([]),
  completionCriteria: z.array(CompletionCriterionSchema).default([]),
  priority: WorkItemPrioritySchema.default('medium'),
  dependencies: z.array(WorkItemDependencySchema).default([]),
  assignedToUserId: z.string().optional(),
});
export type WorkItem = z.infer<typeof WorkItemSchema>;
